import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Image,
  Icon,
  IconButton,
  Circle,
  Button,
  Flex,
  Progress,
  chakra,
} from "@chakra-ui/react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaArrowUp,
  FaTimes,
  FaEllipsisH,
  FaGlobe,
} from "react-icons/fa";
import { FaCheckCircle } from "react-icons/fa";
import type { AdPreviewCollaboration } from "../../utils/adPreviewCollaboration";
import CollaborationIdentityDisplay from "./CollaborationIdentityDisplay";

const Video = chakra("video");

// Helper function to determine if a URL is a video
const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.includes(ext));
};
export interface FacebookStoryPreviewProps {
  message: string;
  ctaText: string;
  mediaUrls: string[];
  mediaType: "single" | "carousel";
  pageName: string;
  profilePictureUrl?: string;
  websiteUrl?: string;
  eventName: string;
  description: string;
  collaboration?: AdPreviewCollaboration;
  fullWidth?: boolean;
}

const FacebookStoryPreview: React.FC<FacebookStoryPreviewProps> = ({
  message,
  ctaText,
  mediaUrls,
  mediaType,
  pageName,
  profilePictureUrl,
  websiteUrl,
  eventName,
  description,
  collaboration,
  fullWidth = false,
}) => {
  const profilePicture = profilePictureUrl; // Facebook pages don't have profile pictures in this context
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [progressValue, setProgressValue] = useState(0);

  // Use the mediaUrls array directly - component determines if it's carousel based on length
  const mediaItems = useMemo(() => mediaUrls || [], [mediaUrls]);
  const currentMedia = mediaItems[currentImageIndex];
  const isCarousel = mediaItems.length > 1;

  // Reset current image index when media changes - only when mediaUrls actually changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setProgressValue(0);
  }, [mediaUrls]);

  // Auto-advance carousel stories
  useEffect(() => {
    if (!isCarousel || mediaItems.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setProgressValue((prev) => {
        const newValue = prev + 2;

        if (newValue >= 100) {
          setCurrentImageIndex((current) => {
            const nextIndex =
              current === mediaItems.length - 1 ? 0 : current + 1;
            return nextIndex;
          });
          return 0;
        }
        return newValue;
      });
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [isCarousel, mediaItems.length]);

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => {
      const newIndex = prev === 0 ? mediaItems.length - 1 : prev - 1;
      return newIndex;
    });
    setProgressValue(0);
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => {
      const newIndex = prev === mediaItems.length - 1 ? 0 : prev + 1;
      return newIndex;
    });
    setProgressValue(0);
  };

  return (
    <Box
      bg="black"
      borderRadius="xl"
      overflow="hidden"
      w="full"
      maxW={fullWidth ? "100%" : ["100%", "70%"]}
      aspectRatio="9/16"
      position="relative"
      boxShadow="0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 16px rgba(0, 0, 0, 0.2)"
    >
      {/* Progress Bar(s) */}
      <Box position="absolute" top={1} left={0} right={0} zIndex={10} px={1}>
        {isCarousel ? (
          <HStack spacing={1} h="3px">
            {mediaItems.map((_, index) => (
              <Box
                key={index}
                flex={1}
                h="full"
                bg="rgba(255, 255, 255, 0.3)"
                borderRadius="full"
              >
                <Box
                  h="full"
                  bg="white"
                  borderRadius="full"
                  width={
                    index < currentImageIndex
                      ? "100%"
                      : index === currentImageIndex
                      ? `${progressValue}%`
                      : "0%"
                  }
                  transition="width 0.1s ease-out"
                />
              </Box>
            ))}
          </HStack>
        ) : (
          <Progress
            value={progressValue}
            size="xs"
            colorScheme="white"
            bg="transparent"
            sx={{
              "& .chakra-progress__filled-track": {
                bg: "white",
              },
            }}
          />
        )}
      </Box>

      {/* Header */}
      <HStack
        position="absolute"
        top="4"
        left="4"
        right="4"
        zIndex={10}
        justifyContent="space-between"
        alignItems="center"
      >
        <HStack spacing={3} alignItems="center" flex={1} minW={0}>
          <CollaborationIdentityDisplay
            platform="facebook"
            tone="dark"
            primaryName={pageName}
            primaryProfilePictureUrl={profilePicture}
            collaboration={collaboration}
            secondaryLabel={collaboration ? undefined : "Sponsored"}
            size="sm"
          />
          {!collaboration && (
            <Icon as={FaCheckCircle} color="blue.400" fontSize="xs" flexShrink={0} />
          )}
        </HStack>

        {/* Action Icons */}
        <HStack spacing={3}>
          <Icon as={FaEllipsisH} color="white" fontSize="sm" />
          <Icon as={FaTimes} color="white" fontSize="sm" />
        </HStack>
      </HStack>

      {/* Main Content Area */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="gray.200"
      >
        {currentMedia ? (
          isVideoUrl(currentMedia) ? (
            <Video
              src={currentMedia}
              w="full"
              h="full"
              objectFit="cover"
              autoPlay
              muted
              loop
            />
          ) : (
            <Image
              src={currentMedia}
              alt="Facebook story content"
              w="full"
              h="full"
              objectFit="cover"
            />
          )
        ) : (
          <Box
            w="full"
            h="full"
            bg="gray.200"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text color="gray.500" fontSize="sm">
              {isCarousel ? "Carousel Content" : "Single Media Content"}
            </Text>
          </Box>
        )}

        {/* Navigation Arrows - Show for carousel with multiple items */}
        {isCarousel && mediaItems.length > 1 && (
          <>
            <IconButton
              aria-label="Previous image"
              icon={<FaChevronLeft />}
              position="absolute"
              left="2"
              top="50%"
              transform="translateY(-50%)"
              size="sm"
              bg="rgba(0, 0, 0, 0.5)"
              color="white"
              borderRadius="full"
              _hover={{ bg: "rgba(0, 0, 0, 0.7)" }}
              onClick={handlePrevious}
              zIndex={15}
            />

            <IconButton
              aria-label="Next image"
              icon={<FaChevronRight />}
              position="absolute"
              right="2"
              top="50%"
              transform="translateY(-50%)"
              size="sm"
              bg="rgba(0, 0, 0, 0.5)"
              color="white"
              borderRadius="full"
              _hover={{ bg: "rgba(0, 0, 0, 0.7)" }}
              onClick={handleNext}
              zIndex={15}
            />
          </>
        )}

        {/* Pagination Dots - Show for carousel with multiple items */}
        {isCarousel && mediaItems.length > 1 && (
          <HStack
            position="absolute"
            bottom="20"
            left="50%"
            transform="translateX(-50%)"
            spacing={1}
            zIndex={15}
          >
            {mediaItems.map((_, index) => (
              <Circle
                key={index}
                size="6px"
                bg={
                  index === currentImageIndex
                    ? "white"
                    : "rgba(255, 255, 255, 0.5)"
                }
                cursor="pointer"
                onClick={() => {
                  setCurrentImageIndex(index);
                  setProgressValue(0);
                }}
                transition="all 0.2s"
                _hover={{ bg: "rgba(255, 255, 255, 0.8)" }}
                boxShadow="0 1px 3px rgba(0, 0, 0, 0.3)"
              />
            ))}
          </HStack>
        )}
      </Box>

      {/* Top Gradient Overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="120px"
        bg="linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)"
        zIndex={5}
        pointerEvents="none"
      />

      {/* Bottom Gradient Overlay */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height="100px"
        bg="linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)"
        zIndex={5}
        pointerEvents="none"
      />

      {/* Bottom Section with CTA - Show for all stories */}
      {mediaItems.length > 0 && (
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          bg="linear-gradient(transparent, rgba(0, 0, 0, 0.8))"
          alignItems={"center"}
          justifyContent={"center"}
          display="flex"
          p={4}
          zIndex={10}
        >
          <VStack gap={0}>
            {/* Swipe Up Indicator */}
            <Flex justifyContent="center" mb={2}>
              <Icon as={FaArrowUp} color="white" fontSize="sm" />
            </Flex>

            {/* CTA Button */}
            <Button
              bg="white"
              color="black"
              borderRadius="full"
              fontWeight="medium"
              fontSize="sm"
              px={6}
              py={2}
              h="auto"
              minW="fit-content"
              _hover={{ bg: "gray.100" }}
            >
              {ctaText}
            </Button>
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default FacebookStoryPreview;
